import {
  ArrowLeft,
  Check,
  ChevronLeft,
  ChevronRight,
  Copy,
  CornerDownLeft,
  SendHorizontal,
  Sparkles,
  X,
} from 'lucide-react';
import { useEffect, type RefObject } from 'react';

import type { AiTextActions } from '~/components/pages/ai/placements/preview/useAiTextActions';
import { Button, Popover, Spinner } from '~/components/ui';
import { isCustomPromptEntryState } from '~/lib/ai/aiWriteState';

const AI_TERMS_URL = 'https://www.backatyou.com/ai-product-terms';
const AI_FEEDBACK_URL = 'https://forms.monday.com/forms/a8ee480668805da4f28e7c6bdcbabc67?r=use1';

/** The AI accent: pink to orange. Merged last on a kit Button, the image covers its solid fill. */
export const AI_GRADIENT_CLASS =
  'bg-linear-to-r from-theme-pink-500 to-orange-400 hover:opacity-90 disabled:opacity-50';

const LINK_CLASS = 'text-brand-500 hover:underline dark:text-brand-400';
const INPUT_CLASS =
  'min-w-0 flex-1 bg-transparent text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none dark:text-white/90 dark:placeholder:text-gray-500';

function AiDisclaimer() {
  return (
    <p className="text-xs italic text-gray-500 dark:text-gray-400">
      AI can make mistakes, check for accuracy.{' '}
      <a className={LINK_CLASS} href={AI_TERMS_URL} rel="noopener noreferrer" target="_blank">
        See terms
      </a>{' '}
      or{' '}
      <a className={LINK_CLASS} href={AI_FEEDBACK_URL} rel="noopener noreferrer" target="_blank">
        share feedback
      </a>
      .
    </p>
  );
}

function ResultActions({ ai, testId }: { ai: AiTextActions; testId: string }) {
  return (
    <div className="flex shrink-0 items-center gap-2">
      <Button
        variant="ghost"
        size="icon"
        onClick={ai.copy}
        ariaLabel={ai.isCopied ? 'Copied' : 'Copy generated text'}
        startIcon={
          ai.isCopied ? (
            <Check className="size-4" aria-hidden="true" />
          ) : (
            <Copy className="size-4" aria-hidden="true" />
          )
        }
        testId={`${testId}-copy`}
      />
      <Button variant="outline" onClick={ai.addBelow} testId={`${testId}-add`}>
        Add below
      </Button>
      <Button
        className={AI_GRADIENT_CLASS}
        onClick={ai.replace}
        endIcon={<CornerDownLeft className="size-4" aria-hidden="true" />}
        testId={`${testId}-replace`}
      >
        Replace
      </Button>
    </div>
  );
}

interface AiWritePopoverProps {
  ai: AiTextActions;
  anchorRef: RefObject<HTMLElement | null>;
  testId: string;
}

/**
 * The AI Write panel, anchored under the field's AI trigger: Custom prompt bar, then
 * Generating (Stop or Esc), then Result with a pager once "More like this" has run more than
 * once, or an inline error with Try again. Only the bare prompt bar closes on an outside
 * click or Escape; a result would be lost, so it closes through its X.
 */
export function AiWritePopover({ ai, anchorRef, testId }: AiWritePopoverProps) {
  const { isGenerating, stop } = ai;
  const isPromptEntry = isCustomPromptEntryState(ai);
  const popoverTestId = `${testId}-popover`;

  useEffect(() => {
    if (!isGenerating) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        stop();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [isGenerating, stop]);

  const onInstructionKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      ai.applyInstruction();
    }
  };

  if (isPromptEntry) {
    return (
      <Popover
        anchorRef={anchorRef}
        open
        onClose={ai.close}
        placement="bottom-end"
        ariaLabel="AI write prompt"
        testId={popoverTestId}
        className="w-full max-w-2xl p-4"
      >
        <div className="flex items-center gap-2">
          <div className="flex flex-1 items-center rounded-lg bg-gray-100 px-4 py-2 focus-within:ring-2 focus-within:ring-brand-500/20 dark:bg-white/5">
            <input
              autoFocus
              aria-label="Custom prompt"
              className={INPUT_CLASS}
              data-testid={`${testId}-instruction`}
              onChange={(event) => ai.setInstruction(event.target.value)}
              onKeyDown={onInstructionKeyDown}
              placeholder="Draft, refine, or reinvent your text…"
              value={ai.instruction}
            />
          </div>
          <Button
            size="icon"
            className={AI_GRADIENT_CLASS}
            ariaLabel="Generate"
            disabled={!ai.instruction.trim()}
            onClick={ai.applyInstruction}
            startIcon={<SendHorizontal className="size-4" aria-hidden="true" />}
            testId={`${testId}-generate`}
          />
        </div>
      </Popover>
    );
  }

  const showPager = ai.resultCount > 1;

  return (
    <Popover
      anchorRef={anchorRef}
      open
      onClose={() => undefined}
      placement="bottom-end"
      ariaLabel="AI write result"
      testId={popoverTestId}
      className="flex w-full max-w-2xl flex-col"
    >
      <div className="relative isolate flex flex-col gap-4 p-4">
        {/* The generating backdrop stays mounted so it fades out when the run ends. */}
        <span
          aria-hidden="true"
          className={`pointer-events-none absolute inset-0 -z-10 overflow-hidden rounded-t-lg transition-opacity duration-300 ease-out ${
            isGenerating ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <span className="absolute inset-0 bg-linear-to-r from-theme-pink-500/10 via-orange-400/10 to-theme-pink-500/10" />
          <span className="absolute inset-0 animate-shimmer bg-linear-to-r from-transparent via-white/60 to-transparent motion-reduce:animate-none dark:via-white/10" />
        </span>

        <div className="flex items-center gap-3">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            {showPager ? (
              <div className="flex shrink-0 items-center gap-1.5">
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7"
                  ariaLabel="Previous result"
                  disabled={isGenerating || ai.resultIndex === 0}
                  onClick={ai.previousResult}
                  startIcon={<ChevronLeft className="size-4" aria-hidden="true" />}
                  testId={`${testId}-previous`}
                />
                <span className="text-sm font-semibold text-gray-800 dark:text-white/90">
                  {ai.resultIndex + 1}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7"
                  ariaLabel="Next result"
                  disabled={isGenerating || ai.resultIndex >= ai.resultCount - 1}
                  onClick={ai.nextResult}
                  startIcon={<ChevronRight className="size-4" aria-hidden="true" />}
                  testId={`${testId}-next`}
                />
                <Sparkles className="size-4 text-theme-pink-500" aria-hidden="true" />
              </div>
            ) : null}
            <p className="min-w-0 flex-1 truncate text-sm font-semibold text-gray-800 dark:text-white/90">
              {showPager ? 'More like this' : ai.title}
            </p>
            {isGenerating ? (
              <>
                <Spinner size="sm" />
                <span role="status" className="sr-only">
                  Generating…
                </span>
              </>
            ) : null}
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="size-7"
            ariaLabel="Close AI write"
            onClick={ai.close}
            startIcon={<X className="size-4" aria-hidden="true" />}
            testId={`${testId}-close`}
          />
        </div>

        <div className="flex flex-col gap-3">
          {ai.errorMessage ? (
            <p
              role="alert"
              className="whitespace-pre-wrap text-sm text-error-600 dark:text-error-400"
              data-testid={`${testId}-error`}
            >
              {ai.errorMessage}
            </p>
          ) : (
            <>
              <p className="whitespace-pre-wrap text-sm text-gray-800 dark:text-white/90">
                {ai.modalText}
              </p>
              <AiDisclaimer />
            </>
          )}
        </div>
      </div>

      <div className="flex items-center justify-end gap-4 border-t border-gray-200 px-4 py-3 dark:border-gray-800">
        {ai.errorMessage ? (
          <Button className={AI_GRADIENT_CLASS} onClick={ai.retry} testId={`${testId}-retry`}>
            Try again
          </Button>
        ) : isGenerating ? (
          <Button variant="outline" onClick={ai.stop} testId={`${testId}-stop`}>
            Stop generation
            <span className="text-gray-400 dark:text-gray-500">Esc</span>
          </Button>
        ) : ai.isInstructionOpen ? (
          <>
            <div className="flex min-w-0 flex-1 items-center gap-3">
              <Button
                variant="outline"
                size="icon"
                ariaLabel="Cancel refinement"
                onClick={ai.closeInstruction}
                startIcon={<ArrowLeft className="size-4" aria-hidden="true" />}
                testId={`${testId}-cancel-refine`}
              />
              <input
                aria-label="Refine the result"
                className={INPUT_CLASS}
                data-testid={`${testId}-instruction`}
                onChange={(event) => ai.setInstruction(event.target.value)}
                onKeyDown={onInstructionKeyDown}
                placeholder="This but…"
                value={ai.instruction}
              />
            </div>
            <ResultActions ai={ai} testId={testId} />
          </>
        ) : (
          <>
            <div className="flex flex-1 items-center gap-3">
              <Button
                variant="outline"
                disabled={!ai.hasGeneratedResult}
                onClick={ai.moreLikeThis}
                testId={`${testId}-more`}
              >
                More like this
              </Button>
              <Button
                variant="outline"
                disabled={!ai.hasGeneratedResult}
                onClick={ai.openInstruction}
                testId={`${testId}-refine`}
              >
                This but…
              </Button>
            </div>
            <ResultActions ai={ai} testId={testId} />
          </>
        )}
      </div>
    </Popover>
  );
}
